import { supabase } from '../lib/supabase'
import { getCurrentUserId } from '../lib/auth'

// ============================================================
// TYPES
// ============================================================

export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'cancelled' | 'unpaid' | 'paused' | 'trial_expired'
export type BillingInterval = 'monthly' | 'annual'
export type PaymentStatus = 'succeeded' | 'failed' | 'refunded' | 'pending'

export interface Plan {
  id: string
  name: string
  stripe_price_id_monthly: string
  stripe_price_id_annual: string
  amount_monthly_pence: number
  amount_annual_pence: number
  max_properties: number
  features: Record<string, unknown>
  is_active: boolean
}

export interface Subscription {
  id: string
  owner_id: string
  plan_id: string
  stripe_customer_id: string
  stripe_subscription_id: string
  status: SubscriptionStatus
  billing_interval: BillingInterval
  current_period_start: string
  current_period_end: string
  trial_start: string | null
  trial_end: string | null
  cancel_at: string | null
  cancelled_at: string | null
  cancellation_reason: string | null
  created_at: string
  updated_at: string
  plan?: Plan
}

export interface Payment {
  id: string
  owner_id: string
  subscription_id: string | null
  stripe_payment_intent_id: string | null
  stripe_invoice_id: string | null
  amount_pence: number
  currency: string
  status: PaymentStatus
  description: string | null
  invoice_pdf_url: string | null
  failure_reason: string | null
  created_at: string
}

export interface SubscriptionStatusInfo {
  isActive: boolean
  isTrial: boolean
  status: SubscriptionStatus | null
  trialDaysRemaining: number
  currentPeriodEnd: string | null
  maxProperties: number
  canCreateProperty: boolean
}

// ============================================================
// PLANS
// ============================================================

export async function getActivePlans(): Promise<Plan[]> {
  const { data, error } = await supabase
    .from('plans')
    .select('*')
    .eq('is_active', true)
    .order('amount_monthly_pence', { ascending: true })

  if (error) throw error
  return data || []
}

export async function getPlan(planId: string): Promise<Plan | null> {
  const { data, error } = await supabase
    .from('plans')
    .select('*')
    .eq('id', planId)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null // Not found
    throw error
  }
  return data
}

// ============================================================
// SUBSCRIPTION STATUS
// ============================================================

export async function getSubscription(): Promise<Subscription | null> {
  const userId = await getCurrentUserId()

  const { data, error } = await supabase
    .from('subscriptions')
    .select(`
      *,
      plan:plans(*)
    `)
    .eq('owner_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) throw error
  return data
}

export async function getSubscriptionStatus(ownerIdOverride?: string): Promise<SubscriptionStatusInfo> {
  const effectiveId = ownerIdOverride ?? await getCurrentUserId()

  // Use the helper function from the database
  const { data, error } = await supabase
    .rpc('get_subscription_status', { p_landlord_id: effectiveId })

  if (error) throw error

  const result = data?.[0]

  if (!result) {
    return {
      isActive: false,
      isTrial: false,
      status: null,
      trialDaysRemaining: 0,
      currentPeriodEnd: null,
      maxProperties: 0,
      canCreateProperty: false,
    }
  }

  // Check if can add property
  const { data: canAdd } = await supabase
    .rpc('can_add_property', { p_landlord_id: effectiveId })

  return {
    isActive: result.is_active,
    isTrial: result.is_trial,
    status: result.status,
    trialDaysRemaining: result.trial_days_remaining,
    currentPeriodEnd: result.current_period_end,
    maxProperties: result.max_properties,
    canCreateProperty: canAdd ?? false,
  }
}

export async function isSubscriptionActive(ownerIdOverride?: string): Promise<boolean> {
  const status = await getSubscriptionStatus(ownerIdOverride)
  return status.isActive
}

export async function canAddProperty(ownerIdOverride?: string): Promise<boolean> {
  const effectiveId = ownerIdOverride ?? await getCurrentUserId()
  const { data, error } = await supabase
    .rpc('can_add_property', { p_landlord_id: effectiveId })

  if (error) throw error
  return data ?? false
}

// ============================================================
// CHECKOUT & BILLING PORTAL
// ============================================================

export interface CreateCheckoutParams {
  planId: string
  interval: BillingInterval
  successUrl?: string
  cancelUrl?: string
  trialDays?: number
}

export interface CheckoutSessionResult {
  url: string
  sessionId: string
}

/**
 * Creates a Stripe Checkout session for subscription.
 * Returns a URL to redirect the user to Stripe Checkout.
 */
export async function createCheckoutSession(
  params: CreateCheckoutParams
): Promise<CheckoutSessionResult> {
  const body: Record<string, unknown> = {
    plan_id: params.planId,
    interval: params.interval,
    success_url: params.successUrl || `${window.location.origin}/billing/success`,
    cancel_url: params.cancelUrl || `${window.location.origin}/billing/checkout`,
  }
  if (params.trialDays !== undefined) {
    body.trial_period_days = params.trialDays
  }

  const { data, error } = await supabase.functions.invoke('create-checkout-session', {
    body,
  })

  if (error) throw error
  if (!data?.url) throw new Error('No checkout URL returned')

  return {
    url: data.url,
    sessionId: data.session_id,
  }
}

/**
 * Creates a Stripe Billing Portal session for managing subscription.
 * Returns a URL to redirect the user to the Stripe portal.
 */
export async function createBillingPortalSession(
  returnUrl?: string
): Promise<string> {
  const { data, error } = await supabase.functions.invoke<{ url?: string; error?: string }>('create-billing-portal-session', {
    body: {
      return_url: returnUrl || `${window.location.origin}/settings/billing`,
    },
  })

  if (data?.error) {
    throw new Error(data.error)
  }

  if (error) {
    const context = (error as { context?: { body?: string } }).context
    if (context?.body) {
      try {
        const parsed = JSON.parse(context.body)
        if (parsed.error) throw new Error(parsed.error)
      } catch { /* fall through */ }
    }
    throw new Error(error.message || 'Failed to open billing portal')
  }

  if (!data?.url) throw new Error('No portal URL returned')

  return data.url
}

// ============================================================
// SUBSCRIPTION MANAGEMENT
// ============================================================

/**
 * Cancel subscription at period end.
 * User keeps access until current billing period ends.
 */
export async function cancelSubscription(
  reason?: string
): Promise<void> {
  const { error } = await supabase.functions.invoke('cancel-subscription', {
    body: {
      cancel_at_period_end: true,
      reason,
    },
  })

  if (error) throw error
}

/**
 * Reactivate a cancelled subscription before period end.
 */
export async function reactivateSubscription(): Promise<void> {
  const { error } = await supabase.functions.invoke('reactivate-subscription', {
    body: {},
  })

  if (error) throw error
}

/**
 * Change billing interval (monthly <-> annual).
 * Handled by Stripe proration.
 */
export async function changeBillingInterval(
  newInterval: BillingInterval
): Promise<void> {
  const { error } = await supabase.functions.invoke('change-billing-interval', {
    body: {
      interval: newInterval,
    },
  })

  if (error) throw error
}

export interface UpgradePreview {
  proration_amount_pence: number
  next_bill_date: string
  next_bill_amount_pence: number
  billing_interval: BillingInterval
  current_plan_name: string
  new_plan_name: string
  new_max_properties: number
}

/**
 * Preview proration costs for upgrading to a new plan.
 * Returns null if preview is unavailable (caller should show fallback text).
 */
export async function previewUpgrade(
  planId: string,
  interval?: BillingInterval
): Promise<UpgradePreview | null> {
  try {
    const { data, error } = await supabase.functions.invoke<UpgradePreview & { error?: string }>('preview-upgrade', {
      body: { plan_id: planId, interval },
    })

    if (error || data?.error) return null
    if (!data || data.proration_amount_pence === undefined) return null

    return data as UpgradePreview
  } catch {
    return null
  }
}

export interface UpgradeResult {
  new_plan_name: string
  new_max_properties: number
  message: string
}

/**
 * Upgrade to a different plan.
 * Prorated charge is applied immediately for the difference.
 */
export async function upgradePlan(
  newPlanId: string,
  interval?: BillingInterval
): Promise<UpgradeResult> {
  const { data, error } = await supabase.functions.invoke<{
    success?: boolean
    error?: string
    new_plan?: { id: string; name: string; max_properties: number }
    message?: string
  }>('upgrade-plan', {
    body: {
      plan_id: newPlanId,
      interval,
    },
  })

  if (data?.error) {
    throw new Error(data.error)
  }

  if (error) {
    const context = (error as { context?: { body?: string } }).context
    if (context?.body) {
      try {
        const parsed = JSON.parse(context.body)
        if (parsed.error) {
          throw new Error(parsed.error)
        }
      } catch {
        // Couldn't parse, fall through
      }
    }
    throw new Error(error.message || 'Failed to upgrade plan')
  }

  return {
    new_plan_name: data?.new_plan?.name || 'new plan',
    new_max_properties: data?.new_plan?.max_properties || 0,
    message: data?.message || 'Plan upgraded successfully',
  }
}

// ============================================================
// PAYMENT HISTORY
// ============================================================

export async function getPayments(): Promise<Payment[]> {
  const userId = await getCurrentUserId()

  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('owner_id', userId)
    .order('created_at', { ascending: false })

  if (error) throw error
  return data || []
}

export async function getPayment(paymentId: string): Promise<Payment | null> {
  const userId = await getCurrentUserId()
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('id', paymentId)
    .eq('owner_id', userId)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    throw error
  }
  return data
}

// ============================================================
// HELPERS
// ============================================================

export { formatPence } from '../utils/currency'

/**
 * Calculate savings percentage for annual vs monthly.
 */
export function calculateAnnualSavings(monthlyPence: number, annualPence: number): number {
  const monthlyAnnualized = monthlyPence * 12
  const savings = ((monthlyAnnualized - annualPence) / monthlyAnnualized) * 100
  return Math.round(savings)
}

/**
 * Get human-readable subscription status.
 */
export function getStatusLabel(status: SubscriptionStatus): string {
  const labels: Record<SubscriptionStatus, string> = {
    trialing: 'Free Trial',
    active: 'Active',
    past_due: 'Past Due',
    cancelled: 'Cancelled',
    unpaid: 'Unpaid',
    paused: 'Paused',
    trial_expired: 'Trial Expired',
  }
  return labels[status] || status
}

/**
 * Get status color for UI.
 */
export function getStatusColor(status: SubscriptionStatus): string {
  const colors: Record<SubscriptionStatus, string> = {
    trialing: 'text-blue-600 bg-blue-50',
    active: 'text-emerald-600 bg-emerald-50',
    past_due: 'text-amber-600 bg-amber-50',
    cancelled: 'text-slate-500 bg-slate-50',
    unpaid: 'text-red-600 bg-red-50',
    paused: 'text-slate-500 bg-slate-50',
    trial_expired: 'text-amber-700 bg-amber-50',
  }
  return colors[status] || 'text-slate-500 bg-slate-50'
}
